import React, { useState, useEffect, useMemo } from 'react';
import { KeycloakService, setDemoRefreshHandler } from '../services/KeycloakService';
import Config from '../config/Config';

// Add demonstrator functionality for caching jwt auth token
const STORAGE_KEY = 'demoAuth';

const saveAuth = (auth) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(auth));
};

const loadAuth = () => {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;

  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const clearAuth = () => {
  localStorage.removeItem(STORAGE_KEY);
};

const isExpired = (token) => {
  try {
    const parsed = parseJwt(token);
    const now = Math.floor(Date.now() / 1000);
    return parsed.exp <= now + 10; // small buffer
  } catch {
    return true;
  }
};

const KeycloakContext = React.createContext(null);

const parseJwt = (token) => {
  const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
  return JSON.parse(atob(base64));
};

export const loginGuestUser = async () => {
  const response = await fetch(
    `${Config.keycloakService.url}/realms/${Config.keycloakService.realm}/protocol/openid-connect/token`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        grant_type: Config.demo.authentication.grant_type,
        client_id: Config.keycloakService.clientId,
        username: Config.demo.authentication.username,
        password: Config.demo.authentication.password,
      }),
    }
  );

  if (!response.ok) {
    throw new Error('Demo login failed');
  }

  return response.json(); // contains access_token + refresh_token
};

const refreshGuestToken = async (refreshToken) => {
  const response = await fetch(
    `${Config.keycloakService.url}/realms/${Config.keycloakService.realm}/protocol/openid-connect/token`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        grant_type: 'refresh_token',
        client_id: Config.keycloakService.clientId,
        refresh_token: refreshToken,
      }),
    }
  );

  if (!response.ok) {
    throw new Error('Refresh failed');
  }

  return response.json();
};

export const KeycloakProvider = ({ children }) => {
  const [initialized, setInitialized] = useState(false);
  const [demoAuth, setDemoAuth] = useState(null);

  useEffect(() => {
    const initialize = async () => {
      const isDemo = Config.isDemo === 'true';

      try {
        if (isDemo) {
          //const tokenResponse = await loginGuestUser();
          let tokenResponse = loadAuth();
          if (
            !tokenResponse ||
            isExpired(tokenResponse.access_token)
          ) {
            tokenResponse = await loginGuestUser();
            saveAuth(tokenResponse);
          }

          const parsed = parseJwt(tokenResponse.access_token);

          KeycloakService.keycloak.token = tokenResponse.access_token;
          KeycloakService.keycloak.tokenParsed = parsed;
          KeycloakService.keycloak.authenticated = true;

          setDemoAuth(tokenResponse);

          // Register a real refresh handler so the axios interceptor in
          // DppManagerService can renew the token on-demand instead of
          // relying solely on the background interval.
          setDemoRefreshHandler(async () => {
            const stored = loadAuth();
            if (stored && !isExpired(stored.access_token)) return;

            let newAuth;
            try {
              if (stored?.refresh_token) {
                newAuth = await refreshGuestToken(stored.refresh_token);
              } else {
                throw new Error('no refresh token');
              }
            } catch {
              newAuth = await loginGuestUser();
            }

            const parsedToken = parseJwt(newAuth.access_token);
            KeycloakService.keycloak.token = newAuth.access_token;
            KeycloakService.keycloak.tokenParsed = parsedToken;
            saveAuth(newAuth);
            setDemoAuth(newAuth);
          });

          setInitialized(true);
          return;
        }

        const authenticated = await KeycloakService.initKeycloak();
        if (authenticated) setInitialized(true);

      } catch (e) {
        console.error(e);
        clearAuth();
      }
    };

    initialize();
  }, []);

  useEffect(() => {
    if (!demoAuth) return;

    const interval = setInterval(async () => {
      try {
        const refreshed = await refreshGuestToken(demoAuth.refresh_token);

        const parsed = parseJwt(refreshed.access_token);

        KeycloakService.keycloak.token = refreshed.access_token;
        KeycloakService.keycloak.tokenParsed = parsed;

        saveAuth(refreshed);
        setDemoAuth(refreshed);
      } catch (e) {
        console.error('Refresh failed, fallback to login', e);

        try {
          const tokenResponse = await loginGuestUser();
          const parsed = parseJwt(tokenResponse.access_token);

          KeycloakService.keycloak.token = tokenResponse.access_token;
          KeycloakService.keycloak.tokenParsed = parsed;

          saveAuth(tokenResponse);
          setDemoAuth(tokenResponse);
        } catch (err) {
          console.error('Fallback login failed', err);
        }
      }
    }, (demoAuth.expires_in - 30) * 1000);

    return () => clearInterval(interval);
  }, [demoAuth]);

  const value = useMemo(() => {
    const isDemo = Config.isDemo === 'true';

    if (isDemo) {
      return {
        ...KeycloakService,
        refreshToken: async () => true, // prevent keycloak-js crash
      };
    }

    return KeycloakService;
  }, []);

  if (!initialized) return <div></div>;

  return (
    <KeycloakContext.Provider value={value}>
      {children}
    </KeycloakContext.Provider>
  );
};

export const useKeycloak = () => {
  const keycloak = React.useContext(KeycloakContext);
  if (!keycloak) {
    throw new Error('useKeycloak must be used within a KeycloakProvider');
  }
  return keycloak;
};